// Preparation claims on the LACW billing week: prep and lump sum preparation fees claimed in
// ATLAS that week - usually for aid approved that week, so for clients who did not appear.
// They are kept apart from the diary appearances (they have no court date) but count in the
// week's totals, print with it and are matched against the ATLAS claims export. A claim
// belongs to the week it was claimed in (Emma, 23 Oct 2026).
//
// The fees come from BILLING_FEE_TABLE (VLA table, ATLAS fee name, amount). Disbursements
// such as psych reports are deliberately not offered.

import { BILLING_FEE_TABLE, feeKey, findBillingFee } from "./billingFeeTable.js";

// Court levels and the VLA tables whose prep fees belong to each.
const LEVEL_OF_TABLE = [
  [/Court of Appeal|Interlocutory/i, "Court of Appeal"],
  [/High Court/i, "High Court"],
  [/Supreme Court/i, "Supreme Court"],
  [/County Court/i, "County Court"],
  [/Magistrates|Children|committal|Family violence|Personal safety|Consolidated extension/i, "Magistrates' / Children's Court"],
];

export const COURT_LEVELS = ["Magistrates' / Children's Court", "County Court", "Supreme Court", "Court of Appeal", "High Court"];

const PREP = /PREPARATION|LUMP SUM/i;

export function courtLevelOfTable(table = "") {
  return LEVEL_OF_TABLE.find(([pattern]) => pattern.test(table))?.[1] || "";
}

// [{ table, fees: [{ key, name, amount }] }] for one court level, in table order.
export function prepFeesFor(level) {
  return Object.entries(BILLING_FEE_TABLE)
    .filter(([table]) => courtLevelOfTable(table) === level)
    .map(([table, rows]) => ({
      table,
      fees: rows.filter(([name]) => PREP.test(name)).map(([name, amount]) => ({ key: feeKey(table, name), name, amount })),
    }))
    .filter(group => group.fees.length > 0);
}

// "SUNSHINE MAGISTRATES' COURT" -> "Magistrates' / Children's Court"
export function courtLevelForCourt(court = "") {
  const text = String(court).toLowerCase();
  if (/court of appeal/.test(text)) return "Court of Appeal";
  if (/high court/.test(text)) return "High Court";
  if (/supreme/.test(text)) return "Supreme Court";
  if (/county/.test(text)) return "County Court";
  if (/magistrate|children/.test(text)) return "Magistrates' / Children's Court";
  return "";
}

export function newPrepClaim(fields = {}) {
  const id = globalThis.crypto?.randomUUID?.() || `prep_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return {
    id,
    client: "",
    grant: "",
    courtLevel: "Magistrates' / Children's Court",
    feeKey: "",
    amount: undefined,
    note: "",
    source: "manual",
    addedAt: new Date().toISOString(),
    ...fields,
  };
}

// The amount claimed: a typed amount if there is one, otherwise the chosen fee's amount.
export function prepClaimAmount(line = {}) {
  if (line.amount != null && line.amount !== "") {
    const typed = Number(line.amount);
    return Number.isFinite(typed) ? typed : null;
  }
  return findBillingFee(line.feeKey)?.amount ?? null;
}

export function prepClaimFeeName(line = {}) {
  return findBillingFee(line.feeKey)?.name || "";
}

export function prepClaimsTotal(lines = []) {
  return Math.round(lines.reduce((sum, line) => sum + (prepClaimAmount(line) ?? 0), 0) * 100) / 100;
}
